import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useAuth } from './AuthContext';
import transactionService from '../services/transactionService';

const TransactionContext = createContext();

/**
 * TransactionProvider — 거래 내역 데이터와 CRUD 작업을 전역으로 관리하는 Provider
 *
 * 제공하는 값:
 * - transactions (Array): 거래 내역 배열
 * - isLoading (boolean): 데이터 로딩 중 여부
 * - fetchTransactions(): 거래 목록 조회
 * - addTransaction(data): 거래 추가
 * - updateTransaction(id, data): 거래 수정
 * - deleteTransaction(id): 거래 삭제
 * - refreshTransactions(): 거래 목록 갱신 (fetchTransactions alias)
 */
export const TransactionProvider = ({ children }) => {
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();

  /**
   * 거래 목록 조회
   * transactionService를 통해 서버에서 거래 내역을 가져온다.
   */
  const fetchTransactions = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await transactionService.getTransactions();
      setTransactions(data);
    } catch (error) {
      console.error('거래 내역 조회 오류:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * 거래 추가
   * @param {object} data - 거래 데이터 (type, amount, category, description, date 등)
   */
  const addTransaction = useCallback(
    async (data) => {
      await transactionService.createTransaction(data);
      await fetchTransactions();
    },
    [fetchTransactions]
  );

  /**
   * 거래 수정
   * @param {number} id - 수정할 거래 ID
   * @param {object} data - 수정할 거래 데이터
   */
  const updateTransaction = useCallback(
    async (id, data) => {
      await transactionService.updateTransaction(id, data);
      await fetchTransactions();
    },
    [fetchTransactions]
  );

  /**
   * 거래 삭제
   * @param {number} id - 삭제할 거래 ID
   */
  const deleteTransaction = useCallback(
    async (id) => {
      await transactionService.deleteTransaction(id);
      await fetchTransactions();
    },
    [fetchTransactions]
  );

  // 마운트 시 user가 존재하면 거래 내역 자동 조회
  useEffect(() => {
    if (user) {
      fetchTransactions();
    }
  }, [user, fetchTransactions]);

  // Context value를 useMemo로 메모이제이션하여 불필요한 리렌더 방지
  const value = useMemo(
    () => ({
      transactions,
      isLoading,
      fetchTransactions,
      addTransaction,
      updateTransaction,
      deleteTransaction,
      refreshTransactions: fetchTransactions,
    }),
    [transactions, isLoading, fetchTransactions, addTransaction, updateTransaction, deleteTransaction]
  );

  return (
    <TransactionContext.Provider value={value}>
      {children}
    </TransactionContext.Provider>
  );
};

/**
 * useTransactions 커스텀 훅
 * TransactionProvider 외부에서 호출 시 에러를 throw한다.
 * @returns {{ transactions, isLoading, fetchTransactions, addTransaction, updateTransaction, deleteTransaction, refreshTransactions }}
 */
export const useTransactions = () => {
  const context = useContext(TransactionContext);
  if (!context) {
    throw new Error('useTransactions must be used within TransactionProvider');
  }
  return context;
};
